/**
 * SourceListVertical Component
 * Displays sources in a vertical panel with header and optional footer content
 * Similar to online-community's SourceListVertical
 */

import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { Icon } from '@hl/ui-components';
import React, { useEffect, useRef } from 'react';

import { MessageReference } from '../../../Message/types/messages';
import { OcTooltip } from '../Shared/OcTooltip';
import SourceCard from './SourceCard';
import {
  SourceListContainer,
  SourceListHeader,
  SourceListScrollContainer,
  SourceListSpacer,
} from './SourceListVertical.styled';

export interface SourceListVerticalProps {
  sources: MessageReference[];
  className?: string;
}

/**
 * SourceListVertical Component
 * Renders a vertical list of sources
 */
const SourceListVertical: React.FC<SourceListVerticalProps> = ({
  sources,
  className = '',
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to top when sources change
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [sources]);

  const tooltipText = 
    "AI messages reference content within this site that you have access to view. External web content is not available to AI. However, it can use the LLM's training data when no relevant community content is available.";

  return (
    <SourceListContainer className={className}>
      <SourceListSpacer/>
      <SourceListHeader>
        {`${sources.length} ${sources.length === 1 ? 'source' : 'sources'}`}
        {' '}
        <OcTooltip 
          overlay={tooltipText} 
          placement="bottom"
          title="Additional information about AI sources"
          className="ai-info-tooltip-container"
        >
          <Icon icon={faInfoCircle} />
        </OcTooltip>
      </SourceListHeader>
      <SourceListScrollContainer ref={scrollContainerRef}>
        {sources.map((source, index) => (
          <SourceCard
            key={`source-${index}`}
            source={source}
            ordinal={index + 1}
          />
        ))}
      </SourceListScrollContainer>
    </SourceListContainer>
  );
};

export default SourceListVertical;
